import { ReactNode, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Btn } from '../actions';
import Card from '../cards/Card';
import { IconAngleDown, IconAngleUp } from '@icons';
import type { SelectFooterAction, SelectOption } from './Select';

export type MultiSelectOption = SelectOption;

export interface MultiSelectOptionGroup {
    label: string;
    options: MultiSelectOption[];
}

export interface MultiSelectProps {
    options: MultiSelectOption[];
    /** When set, dropdown shows section labels and lists all options (selected + unselected) under each group. */
    optionGroups?: MultiSelectOptionGroup[];
    value: string[];
    onChange: (value: string[]) => void;
    placeholder?: string;
    disabled?: boolean;
    hasError?: boolean;
    emptyMessage?: string;
    maxSelections?: number;
    footerAction?: SelectFooterAction;
    className?: string;
    ariaLabel?: string;
    onBlur?: () => void;
    ariaDescribedBy?: string;
    dropdownCardVariant?: 'card-sm' | 'card-lg';
    wrapDropdownInCard?: boolean;
    formatTriggerLabel?: (selectedOptions: MultiSelectOption[]) => string;
    renderOption?: (option: MultiSelectOption, isSelected: boolean) => ReactNode;
}

const defaultFormatTriggerLabel = (selectedOptions: MultiSelectOption[], placeholder: string): string => {
    if (selectedOptions.length === 0) {
        return placeholder;
    }

    if (selectedOptions.length === 1) {
        return selectedOptions[0]?.label || placeholder;
    }

    return `${selectedOptions.length} selected`;
};

const sortOptionsForSelection = (options: MultiSelectOption[], selection: Set<string>) => {
    const byLabel = (a: MultiSelectOption, b: MultiSelectOption) =>
        a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });

    const selected = options.filter((option) => selection.has(option.value)).sort(byLabel);
    const unselected = options.filter((option) => !selection.has(option.value)).sort(byLabel);

    return [...selected, ...unselected];
};

export const MultiSelect = ({
    options,
    optionGroups,
    value,
    onChange,
    placeholder = 'Select...',
    disabled = false,
    hasError = false,
    emptyMessage = 'No options available',
    maxSelections,
    footerAction,
    className,
    ariaLabel,
    onBlur,
    ariaDescribedBy,
    dropdownCardVariant = 'card-sm',
    wrapDropdownInCard = true,
    formatTriggerLabel,
    renderOption,
}: MultiSelectProps) => {
    const generatedId = useId();
    const triggerId = `${generatedId}-trigger`;
    const listboxId = `${generatedId}-listbox`;
    const [isOpen, setIsOpen] = useState(false);
    const [activeOptionIndex, setActiveOptionIndex] = useState(-1);
    const rootRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const selectedSet = useMemo(() => new Set(value), [value]);

    const sortedOptions = useMemo(() => {
        if (optionGroups?.length) {
            return optionGroups.flatMap((group) => sortOptionsForSelection(group.options, selectedSet));
        }

        return sortOptionsForSelection(options, selectedSet);
    }, [optionGroups, options, selectedSet]);

    const selectedOptions = useMemo(
        () => sortedOptions.filter((option) => selectedSet.has(option.value)),
        [selectedSet, sortedOptions],
    );

    const triggerLabel = formatTriggerLabel
        ? formatTriggerLabel(selectedOptions)
        : defaultFormatTriggerLabel(selectedOptions, placeholder);

    const hasSelection = selectedOptions.length > 0;
    const atLimit = typeof maxSelections === 'number' && value.length >= maxSelections;

    const closeDropdown = () => {
        setIsOpen(false);
        setActiveOptionIndex(-1);
    };

    const getInitialActiveIndex = () => {
        if (sortedOptions.length === 0) return -1;
        const firstSelected = sortedOptions.findIndex((option) => selectedSet.has(option.value));
        return firstSelected >= 0 ? firstSelected : 0;
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (rootRef.current?.contains(event.target as Node)) return;
            closeDropdown();
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (!isOpen) {
            setActiveOptionIndex(-1);
        }
    }, [isOpen]);

    useEffect(() => {
        if (activeOptionIndex < 0 || !dropdownRef.current) return;
        const activeEl = dropdownRef.current.querySelector(`[data-option-index="${activeOptionIndex}"]`);
        activeEl?.scrollIntoView({ block: 'nearest' });
    }, [activeOptionIndex]);

    const rootClassName = [
        'select-component',
        'multi-select-component',
        isOpen ? 'is-open' : '',
        disabled ? 'is-disabled' : '',
        hasError ? 'has-error' : '',
        className,
    ]
        .filter(Boolean)
        .join(' ');

    const toggleOption = (optionValue: string) => {
        let nextIds: string[];

        if (selectedSet.has(optionValue)) {
            nextIds = value.filter((id) => id !== optionValue);
        } else {
            if (atLimit) {
                return;
            }
            nextIds = [...value, optionValue];
        }

        onChange(nextIds);

        const nextSorted = sortOptionsForSelection(options, new Set(nextIds));
        const nextIndex = nextSorted.findIndex((option) => option.value === optionValue);
        if (nextIndex >= 0) {
            setActiveOptionIndex(nextIndex);
        }
    };

    const renderOptionContent = (option: MultiSelectOption, isSelected: boolean) => {
        if (renderOption) {
            return renderOption(option, isSelected);
        }

        return (
            <>
                <span
                    className={[
                        'multi-select-component-check',
                        isSelected ? 'is-checked' : '',
                    ]
                        .filter(Boolean)
                        .join(' ')}
                    aria-hidden="true"
                >
                    {isSelected ? '✓' : ''}
                </span>
                {option.content ?? <span className="select-component-option-label">{option.label}</span>}
            </>
        );
    };

    const isOptionAtSelectionLimit = (isSelected: boolean) =>
        !isSelected && atLimit;

    const renderListboxOption = (option: MultiSelectOption, index: number) => {
        const isSelected = selectedSet.has(option.value);
        const isDisabledOption = isOptionAtSelectionLimit(isSelected);

        return (
            <div
                key={option.value}
                id={`${generatedId}-option-${index}`}
                data-option-index={index}
                role="option"
                aria-selected={isSelected}
                aria-disabled={isDisabledOption ? true : undefined}
                className={[
                    'select-component-option',
                    'multi-select-component-option',
                    isSelected ? 'is-selected' : '',
                    isDisabledOption ? 'is-disabled' : '',
                    index === activeOptionIndex ? 'is-active' : '',
                ]
                    .filter(Boolean)
                    .join(' ')}
                onMouseDown={(event) => event.preventDefault()}
                onMouseEnter={() => setActiveOptionIndex(index)}
                onClick={() => {
                    if (isDisabledOption) return;
                    toggleOption(option.value);
                }}
            >
                {renderOptionContent(option, isSelected)}
            </div>
        );
    };

    const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
        if (disabled) return;

        if (event.key === 'Escape') {
            closeDropdown();
            return;
        }

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            if (sortedOptions.length === 0) return;

            setIsOpen(true);
            setActiveOptionIndex((current) => {
                if (!isOpen || current < 0) return getInitialActiveIndex();
                return (current + 1) % sortedOptions.length;
            });
            return;
        }

        if (event.key === 'ArrowUp') {
            event.preventDefault();
            if (sortedOptions.length === 0) return;

            setIsOpen(true);
            setActiveOptionIndex((current) => {
                if (!isOpen || current < 0) return sortedOptions.length - 1;
                return current <= 0 ? sortedOptions.length - 1 : current - 1;
            });
            return;
        }

        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();

            if (!isOpen) {
                setIsOpen(true);
                setActiveOptionIndex(getInitialActiveIndex());
                return;
            }

            if (activeOptionIndex >= 0 && sortedOptions[activeOptionIndex]) {
                const option = sortedOptions[activeOptionIndex];
                if (!selectedSet.has(option.value) && atLimit) {
                    return;
                }
                toggleOption(option.value);
            }
        }
    };

    const dropdownBody = (
        <>
            {sortedOptions.length === 0 && <div className="select-component-empty">{emptyMessage}</div>}
            {optionGroups?.length
                ? optionGroups.map((group) => {
                      const groupOptions = sortOptionsForSelection(group.options, selectedSet);

                      if (groupOptions.length === 0) {
                          return null;
                      }

                      return (
                          <div key={group.label} className="multi-select-component-group" role="presentation">
                              <div className="multi-select-component-group-label">{group.label}</div>
                              {groupOptions.map((option) => {
                                  const index = sortedOptions.findIndex((entry) => entry.value === option.value);
                                  if (index < 0) {
                                      return null;
                                  }

                                  return renderListboxOption(option, index);
                              })}
                          </div>
                      );
                  })
                : sortedOptions.map((option, index) => renderListboxOption(option, index))}
            {footerAction && (
                <div className="select-component-footer" onMouseDown={(event) => event.preventDefault()}>
                    <Btn
                        variant="secondary"
                        buttonSize="sm"
                        buttonText={footerAction.label}
                        buttonOnClick={() => {
                            footerAction.onClick();
                            closeDropdown();
                        }}
                        buttonClasses="select-component-footer-btn"
                    />
                </div>
            )}
        </>
    );

    return (
        <div ref={rootRef} className={rootClassName}>
            <button
                id={triggerId}
                type="button"
                className="select-component-trigger"
                disabled={disabled}
                role="combobox"
                aria-label={ariaLabel}
                aria-describedby={ariaDescribedBy}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                aria-controls={listboxId}
                aria-activedescendant={
                    isOpen && activeOptionIndex >= 0 ? `${generatedId}-option-${activeOptionIndex}` : undefined
                }
                onClick={() => {
                    if (disabled) return;
                    setIsOpen((open) => {
                        if (open) {
                            setActiveOptionIndex(-1);
                            return false;
                        }
                        setActiveOptionIndex(getInitialActiveIndex());
                        return true;
                    });
                }}
                onBlur={onBlur}
                onKeyDown={handleTriggerKeyDown}
            >
                <span className={hasSelection ? 'select-component-value' : 'select-component-placeholder'}>
                    {triggerLabel}
                </span>
                <span className="select-component-caret-wrap" aria-hidden="true">
                    {isOpen ? (
                        <IconAngleUp iconClasses="select-component-caret" />
                    ) : (
                        <IconAngleDown iconClasses="select-component-caret" />
                    )}
                </span>
            </button>

            {isOpen &&
                !disabled &&
                (wrapDropdownInCard ? (
                    <Card
                        id={listboxId}
                        variant={dropdownCardVariant}
                        className="select-component-dropdown"
                        role="listbox"
                        aria-multiselectable="true"
                    >
                        <div ref={dropdownRef}>{dropdownBody}</div>
                    </Card>
                ) : (
                    <div
                        ref={dropdownRef}
                        id={listboxId}
                        className="select-component-dropdown"
                        role="listbox"
                        aria-multiselectable="true"
                    >
                        {dropdownBody}
                    </div>
                ))}
        </div>
    );
};
